import { useState } from "react";
import { hotList, topTags, type HotItem } from "@/mocks/kanliao";

function TrendIcon({ trend }: { trend: HotItem["trend"] }) {
  if (trend === "up") {
    return (
      <span className="grid h-5 w-5 items-center justify-center rounded bg-primary-100 text-primary-600">
        <i className="ri-arrow-up-line text-xs" aria-hidden="true"></i>
      </span>
    );
  }
  if (trend === "down") {
    return (
      <span className="grid h-5 w-5 items-center justify-center rounded bg-secondary-100 text-secondary-700">
        <i className="ri-arrow-down-line text-xs" aria-hidden="true"></i>
      </span>
    );
  }
  return (
    <span className="grid h-5 w-5 items-center justify-center rounded bg-secondary-100 text-secondary-500">
      <i className="ri-subtract-line text-xs" aria-hidden="true"></i>
    </span>
  );
}

export default function HotRank() {
  const [active, setActive] = useState("heat");

  const columns: HotItem[][] = [hotList.slice(0, 6), hotList.slice(6, 12)];

  return (
    <section id="today" className="scroll-mt-24 bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        {/* header */}
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="mb-3 flex items-center gap-2">
              <span className="grid h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-fire-fill text-lg" aria-hidden="true"></i>
              </span>
              <span className="font-label text-xs font-semibold uppercase tracking-[0.24em] text-primary-600">
                今日热度榜 · LIVE
              </span>
            </div>
            <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              现在全网都在聊哪颗瓜
            </h2>
            <p className="mt-2 max-w-xl text-sm text-secondary-700 md:text-base">
              综合热搜、论坛与短视频讨论量实时聚合的今日热度排行，带你看清风向，不错过任何正在升温的猛料。
            </p>
          </div>

          {/* segmented tabs */}
          <div className="inline-flex shrink-0 items-center gap-1 self-start rounded-full border border-background-200 bg-background-100 p-1">
            {topTags.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setActive(t.key)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  active === t.key
                    ? "bg-foreground-950 text-background-50"
                    : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {t.name}
              </button>
            ))}
          </div>
        </div>

        {/* two-column ranking */}
        <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-2 lg:gap-8">
          {columns.map((col, ci) => (
            <div key={ci} className="flex flex-col gap-2">
              {col.map((item) => {
                const top = item.rank <= 3;
                const showBar = active === "heat";
                return (
                  <a
                    href="#leaks"
                    key={item.id}
                    className="group flex items-center gap-3 rounded-xl bg-background-100 p-3 transition-colors hover:bg-background-200/60 md:gap-4 md:p-3.5"
                  >
                    <span
                      className={`grid h-9 w-9 shrink-0 items-center justify-center rounded-lg font-heading text-base font-black ${
                        top ? "bg-accent-500 text-foreground-950" : "bg-background-50 text-secondary-700"
                      }`}
                    >
                      {String(item.rank).padStart(2, "0")}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-foreground-900 group-hover:text-primary-700 md:text-[15px]">
                        {item.title}
                      </p>
                      <div className="mt-1 flex items-center gap-3">
                        <span className="rounded-full bg-background-50 px-2 py-0.5 text-[11px] font-medium text-secondary-700">
                          {item.category}
                        </span>
                        {showBar && (
                          <span className="hidden h-1.5 w-20 overflow-hidden rounded-full bg-background-200 sm:block">
                            <span
                              className="block h-full rounded-full bg-primary-500/80"
                              style={{ width: `${Math.min(100, (item.heat / 10000) * 100)}%` }}
                            ></span>
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-secondary-600">{item.delta}</span>
                        <TrendIcon trend={item.trend} />
                      </div>
                      <span className="text-[11px] font-medium text-secondary-600">{item.heat.toLocaleString()} 热度</span>
                    </div>
                  </a>
                );
              })}
            </div>
          ))}
        </div>

        <p className="mt-6 text-center text-xs text-secondary-600">
          榜单每 5 分钟聚合刷新一次，数据用于洞察当前吃瓜方向，非立场表达。
        </p>
      </div>
    </section>
  );
}